import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import draggable from 'vuedraggable'
import { designSizeFor, toDesignSpace, effectiveLayers, cropsToOverlay, stageLayout } from '@/util/overlayGeometry'
import { loadFabric, loadFonts, createLayerObject, LAYER_HANDLE_STYLE } from '@/util/overlayRenderer'

interface OverlayRow {
  id?: number
  name?: string
  layers: string | any[]
  aspect_ratio: string
  reference_width: number
  reference_height: number
  crop_to_overlay?: number | boolean
}

interface PhotoRow {
  uploaded_image: string
  overlay_id?: number | null
  overlay_layers?: string | null
  isProcessingOverlay?: boolean
}

interface LayerRow {
  index: number
  label: string
  type: string
  hidden: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AdjustOverlayDrawer',
  props: {
    show: { type: Boolean },
    photoUrl: { default: '' },
    overlay: { default: null },
    override: { default: null },
    photos: { default: null },
    photoIndex: { default: 0 },
    overlays: { default: () => [] },
    pending: { type: Boolean, default: false },
    resolveText: { type: Function, default: undefined }
  },
  emits: ["close", "apply", "delete", "clear-overlay", "update:photoIndex", "retake", "use"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'apply', payload: { overlayLayers: string; photoIndex: number; overlay: OverlayRow }): void
  (e: 'delete', photoIndex: number): void
  (e: 'clear-overlay', photoIndex: number): void
  (e: 'update:photoIndex', photoIndex: number): void
  (e: 'retake'): void
  (e: 'use', payload: { overlay: OverlayRow | null; overlayLayers: string | null }): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { show: boolean, photoUrl: string, overlay: OverlayRow | null, override: string | null, photos: PhotoRow[] | null, photoIndex: number, overlays: OverlayRow[], pending: boolean, resolveText: (content: string) => Promise<string> | string }

// Per photo overlay adjustment. The photo is the canvas backdrop, positioned
// with the exact inverse of the bake's fit transform, so a layer dragged here
// lands at the same spot on the baked photo. Layers come from the photo's
// stored adjustment when it has one, otherwise from the overlay template.
// Only position, size, stacking order and visibility change here; adding
// layers or editing text belongs to the overlay editor.
//
// Two ways to use it:
// - single photo (Photos page card, tracker photo step): `photoUrl`, `overlay`,
//   `override`; the parent owns which overlay is on the photo.
// - browse (simple tracker): `photos`, `photoIndex`, `overlays`; the drawer
//   pages through the booking's photos, the select picks the overlay, and
//   Apply reports which overlay and which photo it baked.
// - pending (review before saving): browse mode plus `pending`, with one
//   unsaved photo (an object URL). No paging or delete, the X only clears the
//   stage, and the footer is Retake / Use photo. `use` carries the chosen
//   overlay and, only when the stage was touched, the adjusted layers.




const stage = ref<HTMLElement | null>(null)
const canvasEl = ref<HTMLCanvasElement | null>(null)
const layers = ref<LayerRow[]>([])
const activeIndex = ref<number>(-1)
const ready = ref(false)
const applying = ref(false)
const index = ref(props.photoIndex)
const selectedOverlayId = ref<number | ''>('')
// True once a layer was moved, resized, hidden or reordered since the last build.
const dirty = ref(false)

const browse = computed(() => Array.isArray(props.photos))
const photos = computed<PhotoRow[]>(() => props.photos || [])
const currentPhoto = computed<PhotoRow | null>(() => (browse.value ? photos.value[index.value] || null : null))
const processing = computed(() => !!(currentPhoto.value && currentPhoto.value.isProcessingOverlay))
const activePhotoUrl = computed(() => (browse.value ? (currentPhoto.value ? currentPhoto.value.uploaded_image : '') : props.photoUrl))
const activeOverlay = computed<OverlayRow | null>(() => {
  if (!browse.value) return props.overlay
  if (selectedOverlayId.value === '') return null
  return props.overlays.find(o => o.id === selectedOverlayId.value) || null
})
const drawerTitle = computed(() => (props.pending ? 'Review photo' : browse.value ? 'Photo' : 'Adjust overlay'))

let fabric: any = null
let canvas: any = null
let design = designSizeFor('16:9')
// The stage shows the whole photo in design units; the overlay's artboard sits
// centered inside it (the bake's contain fit) and layers are stored relative to
// it. Layers may be dragged anywhere on the photo; the bake renders the whole
// photo area too. With crop on, the stage is the artboard only.
let frame = { width: design.width, height: design.height }
let artOffset = { x: 0, y: 0 }
const GUIDE_COUNT = 1
let buildToken = 0

function layerLabel(layer: any): string {
  if (layer.type === 'text') return String(layer.content || '').trim() || 'Text'
  return layer.file_name || 'Image'
}

// The adjustment stored on the current photo applies only to the overlay it was made for.
function overrideFor(overlay: OverlayRow | null): string | null {
  if (!browse.value) return props.override || null
  const photo = currentPhoto.value
  if (!photo || !overlay || photo.overlay_id !== overlay.id) return null
  return photo.overlay_layers || null
}

async function build(overrideJson: string | null) {
  const token = ++buildToken
  ready.value = false
  dirty.value = false
  layers.value = []
  activeIndex.value = -1
  teardownCanvas()
  const overlay = activeOverlay.value
  if (!overlay) return
  await nextTick()
  if (token !== buildToken || !canvasEl.value) return

  fabric = await loadFabric()
  if (token !== buildToken) return
  design = designSizeFor(overlay.aspect_ratio)
  canvas = new fabric.Canvas(canvasEl.value, { preserveObjectStacking: true, selection: false })
  canvas.on('selection:created', (e: any) => onSelection(e.selected && e.selected[0]))
  canvas.on('selection:updated', (e: any) => onSelection(e.selected && e.selected[0]))
  canvas.on('selection:cleared', () => onSelection(null))
  canvas.on('object:modified', (e: any) => { dirty.value = true; onObjectModified(e.target) })

  // Backdrop: the whole photo, scaled so the design artboard sits inside it
  // exactly as fitTransform places the artboard on the photo at bake time.
  frame = { width: design.width, height: design.height }
  artOffset = { x: 0, y: 0 }
  try {
    const img = await fabric.FabricImage.fromURL(activePhotoUrl.value, { crossOrigin: 'anonymous' })
    if (token !== buildToken) return
    // Crop on: the stage is the artboard itself, filled by the photo's centered area (the bake's crop).
    const layout = stageLayout(design, img.width, img.height, cropsToOverlay(overlay))
    frame = layout.frame
    artOffset = layout.artOffset
    img.set({ originX: 'left', originY: 'top', scaleX: layout.scale, scaleY: layout.scale, left: layout.backdrop.left, top: layout.backdrop.top })
    canvas.backgroundImage = img
  } catch (e) {
    console.error('Adjust overlay: photo failed to load', e)
  }
  addArtboardGuides()

  const eff = effectiveLayers(overlay, overrideJson)
  const ordered = [...eff.layers].sort((a: any, b: any) => Number(a.position) - Number(b.position))
  const converted = toDesignSpace(ordered, eff.referenceWidth, eff.referenceHeight, design)
  await loadFonts(converted.filter((l: any) => l.type === 'text').map((l: any) => l.font))
  if (token !== buildToken) return

  const rows: LayerRow[] = []
  for (let i = 0; i < converted.length; i++) {
    const layer = converted[i]
    try {
      const opts: { text?: string } = {}
      if (layer.type === 'text') {
        opts.text = props.resolveText ? await props.resolveText(layer.content || '') : layer.content || ''
      }
      const obj = await createLayerObject(fabric, layer, opts)
      if (token !== buildToken) return
      obj.set(LAYER_HANDLE_STYLE)
      obj.set({ visible: layer.hidden !== true, lockUniScaling: true, left: obj.left + artOffset.x, top: obj.top + artOffset.y })
      if (layer.type === 'text') obj.set({ editable: false })
      obj.layerIndex = i
      obj.layerSource = layer
      canvas.add(obj)
      rows.push({ index: i, label: layerLabel(layer), type: layer.type, hidden: layer.hidden === true })
    } catch (e) {
      console.error('Adjust overlay: layer failed to load', e)
    }
  }
  // The list reads top first, like the overlay editor; canvas index 0 is the bottom.
  layers.value = rows.reverse()
  fitCanvas()
  ready.value = true
}

function fitCanvas() {
  if (!canvas || !stage.value) return
  const zoom = Math.min((stage.value.clientWidth - 32) / frame.width, 480 / frame.height)
  canvas.setDimensions({ width: Math.round(frame.width * zoom), height: Math.round(frame.height * zoom) })
  canvas.setZoom(zoom)
  canvas.requestRenderAll()
}

// Outline the overlay's designed area on the photo. One non interactive object,
// always at the bottom of the stack.
function addArtboardGuides() {
  if (!canvas) return
  const { x, y } = artOffset
  canvas.add(new fabric.Rect({
    left: x, top: y, width: design.width, height: design.height, fill: 'transparent',
    stroke: 'rgba(255, 255, 255, 0.6)', strokeWidth: 2, strokeDashArray: [8, 6], strokeUniform: true,
    selectable: false, evented: false, excludeFromExport: true, objectCaching: false,
  }))
}

function teardownCanvas() {
  if (canvas) {
    try { canvas.dispose() } catch (e) { /* already gone */ }
    canvas = null
  }
}

function objectAt(index: number) {
  if (!canvas) return null
  return canvas.getObjects().find((o: any) => o.layerIndex === index) || null
}

function onSelection(obj: any) {
  activeIndex.value = obj && typeof obj.layerIndex === 'number' ? obj.layerIndex : -1
}

function onObjectModified(obj: any) {
  if (!obj) return
  if (obj.layerType === 'text' && (obj.scaleX !== 1 || obj.scaleY !== 1)) {
    obj.set({ fontSize: Math.round(obj.fontSize * obj.scaleY), scaleX: 1, scaleY: 1 })
    obj.setCoords()
    canvas.requestRenderAll()
  }
}

function selectLayer(index: number) {
  const obj = objectAt(index)
  if (!obj || !canvas) return
  if (obj.visible === false) return
  canvas.setActiveObject(obj)
  canvas.requestRenderAll()
  activeIndex.value = index
}

function clearSelection() {
  if (!canvas) return
  canvas.discardActiveObject()
  canvas.requestRenderAll()
  activeIndex.value = -1
}

function toggleHidden(index: number) {
  const obj = objectAt(index)
  const row = layers.value.find(l => l.index === index)
  if (!obj || !row) return
  row.hidden = !row.hidden
  dirty.value = true
  obj.set({ visible: !row.hidden })
  if (row.hidden && canvas.getActiveObject() === obj) {
    canvas.discardActiveObject()
    activeIndex.value = -1
  }
  canvas.requestRenderAll()
}

function onLayerReorder() {
  if (!canvas) return
  dirty.value = true
  const count = layers.value.length
  layers.value.forEach((row, listIndex) => {
    const obj = objectAt(row.index)
    if (obj) canvas.moveObjectTo(obj, GUIDE_COUNT + count - 1 - listIndex)
  })
  canvas.requestRenderAll()
}

function nudge(dx: number, dy: number) {
  const obj = canvas && canvas.getActiveObject()
  if (!obj || typeof obj.layerIndex !== 'number') return
  obj.set({ left: Math.round(obj.left + dx), top: Math.round(obj.top + dy) })
  obj.setCoords()
  dirty.value = true
  canvas.requestRenderAll()
}

function onKeydown(e: KeyboardEvent) {
  if (!props.show) return
  if (e.key === 'Escape') {
    emit('close')
    return
  }
  const target = e.target as HTMLElement | null
  const tag = (target && target.tagName) || ''
  const inField = tag === 'INPUT' || tag === 'TEXTAREA' || (target && target.isContentEditable)
  if (inField || !e.key.startsWith('Arrow')) return
  e.preventDefault()
  const hasSelection = !!(canvas && canvas.getActiveObject())
  // With no layer selected the arrows page through the booking's photos.
  if (!hasSelection && browse.value) {
    if (e.key === 'ArrowLeft') goTo(index.value - 1)
    else if (e.key === 'ArrowRight') goTo(index.value + 1)
    return
  }
  const step = e.shiftKey ? 10 : 1
  const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
  const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
  nudge(dx, dy)
}

function resetToTemplate() {
  build(null)
}

// Browse mode -------------------------------------------------------------
function openPhoto(i: number) {
  index.value = i
  const photo = currentPhoto.value
  const match = photo && photo.overlay_id ? props.overlays.find(o => o.id === photo.overlay_id) : null
  selectedOverlayId.value = match && typeof match.id === 'number' ? match.id : ''
  build(overrideFor(activeOverlay.value))
}

function goTo(i: number) {
  if (i < 0 || i >= photos.value.length || i === index.value) return
  openPhoto(i)
  emit('update:photoIndex', i)
}

// A different overlay starts from its template; the photo's own overlay shows
// its saved adjustment. Nothing is saved until Apply, so no confirm is needed here.
function onOverlayPicked() {
  build(overrideFor(activeOverlay.value))
}

// In pending mode nothing is saved yet, so clearing only empties the stage.
function onClearClick() {
  if (props.pending) {
    selectedOverlayId.value = ''
    build(null)
    return
  }
  emit('clear-overlay', index.value)
}

function onUse() {
  if (applying.value) return
  const overlay = activeOverlay.value
  if (overlay && (!canvas || !ready.value)) return
  applying.value = true
  emit('use', { overlay, overlayLayers: overlay && dirty.value ? serialize() : null })
}

// The adjustment is stored in the design space of the overlay's aspect ratio,
// so reading it back through toDesignSpace is the identity.
function serialize(): string {
  const out = canvas.getObjects().filter((obj: any) => typeof obj.layerIndex === 'number').map((obj: any, index: number) => {
    const src = obj.layerSource || {}
    const isText = obj.layerType === 'text'
    const layer: any = {
      ...src,
      position: index,
      x: Math.round(obj.left - artOffset.x),
      y: Math.round(obj.top - artOffset.y),
      width: Math.round(obj.getScaledWidth()),
      height: Math.round(obj.getScaledHeight()),
      hidden: obj.visible === false,
    }
    if (isText) layer.font_size = Math.round(obj.fontSize)
    delete layer.fontSize
    return layer
  })
  return JSON.stringify({ reference_width: design.width, reference_height: design.height, layers: out })
}

function onApply() {
  if (!canvas || !ready.value || !activeOverlay.value) return
  applying.value = true
  emit('apply', { overlayLayers: serialize(), photoIndex: index.value, overlay: activeOverlay.value })
}

function reset() {
  buildToken++
  teardownCanvas()
  layers.value = []
  activeIndex.value = -1
  ready.value = false
  applying.value = false
  dirty.value = false
  selectedOverlayId.value = ''
}

watch(() => props.show, async (open) => {
  if (open) {
    document.addEventListener('keydown', onKeydown)
    await nextTick()
    if (browse.value) openPhoto(props.photoIndex)
    else build(props.override || null)
  } else {
    document.removeEventListener('keydown', onKeydown)
    reset()
  }
})

watch(() => props.photoIndex, (i) => {
  if (props.show && browse.value && i !== index.value) openPhoto(i)
})

// After a delete the parent shrinks the array: close on empty, else clamp.
watch(() => photos.value.length, (n) => {
  if (!props.show || !browse.value) return
  if (n === 0) {
    emit('close')
  } else if (index.value >= n) {
    openPhoto(n - 1)
    emit('update:photoIndex', n - 1)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  teardownCanvas()
})

return { __sfc: true,props, emit, stage, canvasEl, layers, activeIndex, ready, applying, index, selectedOverlayId, dirty, browse, photos, currentPhoto, processing, activePhotoUrl, activeOverlay, drawerTitle, fabric, canvas, design, frame, artOffset, GUIDE_COUNT, buildToken, layerLabel, overrideFor, build, fitCanvas, addArtboardGuides, teardownCanvas, objectAt, onSelection, onObjectModified, selectLayer, clearSelection, toggleHidden, onLayerReorder, nudge, onKeydown, resetToTemplate, openPhoto, goTo, onOverlayPicked, onClearClick, onUse, serialize, onApply, reset, draggable }
}

})